// Offline scan orchestration: resolve the user from cache, apply the same
// business rules as the backend (via offlineScanLogic), persist the local state
// change, and enqueue a pending scan for later background sync.

import {
  getMeta,
  getRegistration as getCachedRegistration,
  putRegistration,
  enqueuePendingScan,
} from './offlineDb';
import { evaluateScan, applyMutation } from './offlineScanLogic';
import { generateQrBase64 } from './qrCode';
import { getScannerName } from './scannerName';

// Mirrors backend QRUtils.decodeBase64QRCode, which currently returns the payload
// unchanged (the QR content is the plain userId). Kept as a seam in case the
// backend re-enables encryption later.
function decodeQr(qrData) {
  return (qrData || '').toString().trim();
}

let clientScanSeq = 0;
function newClientScanId() {
  clientScanSeq += 1;
  return `${Date.now()}-${clientScanSeq}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Perform a scan entirely against the local cache.
 * @returns a ScanQRResponse-shaped object (same fields the UI expects online).
 */
export async function scanOffline(eventId, { qrData, userId, purpose, scanMode = 'qr', actorId = null }) {
  // Face recognition needs the server-side model; it cannot run offline.
  if (scanMode === 'face') {
    return {
      error: 'Offline',
      message: 'Face recognition is unavailable offline. Use QR or manual entry.',
      purpose,
      offline: true,
    };
  }

  const resolvedUserId = (userId && userId.toString().trim()) || decodeQr(qrData);

  const meta = (await getMeta(eventId)) || {};
  const purposes = meta.purposes || [];
  const formSchema = meta.formSchema || [];
  const purposeConfig = purposes.find(
    (p) => (p.purpose || '').toLowerCase() === (purpose || '').toLowerCase()
  );

  const registration = await getCachedRegistration(eventId, resolvedUserId);

  const { recorded, mutation, response } = evaluateScan({
    registration,
    purposeConfig,
    formSchema,
    purpose,
    userId: resolvedUserId,
    actorId,
  });

  if (recorded && registration) {
    // Persist the local state change so repeat offline scans dedupe correctly.
    const updated = applyMutation(registration, mutation);
    await putRegistration(updated);

    // Queue for background sync. We replay the exact scan (by userId) so the
    // backend re-runs its own authoritative validation on reconnect.
    await enqueuePendingScan({
      eventId,
      userId: registration.userId,
      qrData: registration.userId,
      purpose,
      scanMode,
      clientScanId: newClientScanId(),
      scannerName: getScannerName() || undefined,
    });
  }

  // The backend returns a server-generated QR PNG on success; offline we generate
  // the same QR (plain userId) on the client so badge printing still works.
  if (!response.error && resolvedUserId && !response.qrCodeBase64) {
    response.qrCodeBase64 = await generateQrBase64(resolvedUserId);
  }

  return { ...response, offline: true };
}
